<template>
  <div class="settings-locations-section">
    <SettingsSectionHeader
      title="Locations"
      subtitle="Offices and work sites"
      icon="pi-map-marker"
    >
      <template #actions>
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadLocations"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-locations-section__body">
      <div v-if="loading && !locations.length" class="settings-locations-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-locations-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadLocations" />
        </div>
      </Message>

      <template v-else>
        <section class="settings-locations-section__quick-add" aria-label="Quick add location">
          <div class="settings-locations-section__quick-add-title">Quick add</div>
          <form class="settings-locations-section__quick-add-form" @submit.prevent="handleQuickAdd">
            <FloatLabel variant="on" class="settings-locations-section__quick-add-name">
              <InputText
                id="location-quick-name"
                v-model="quickAdd.name"
                maxlength="160"
                autocomplete="off"
                :disabled="creating"
                class="w-full rounded-xl"
              />
              <label for="location-quick-name" class="text-sm font-medium">
                Name <span class="text-red-500">*</span>
              </label>
            </FloatLabel>
            <FloatLabel variant="on" class="settings-locations-section__quick-add-type">
              <Select
                inputId="location-quick-type"
                v-model="quickAdd.type"
                :options="locationTypeOptions"
                optionLabel="label"
                optionValue="value"
                :disabled="creating"
                class="w-full rounded-xl"
              />
              <label for="location-quick-type" class="text-sm font-medium">Type</label>
            </FloatLabel>
            <Button
              type="submit"
              label="Add"
              icon="pi pi-plus"
              :loading="creating"
              :disabled="!quickAdd.name?.trim()"
            />
          </form>
        </section>

        <SettingsOrgReferenceToolbar
          v-model:show-inactive="showInactive"
          v-model:search-query="searchQuery"
          :count-label="countLabel"
          inactive-input-id="locations-show-inactive"
          search-placeholder="Search locations..."
          :loading="loading"
        />

        <div
          v-if="!filteredLocations.length"
          class="settings-locations-section__empty"
        >
          <i class="pi pi-map-marker settings-locations-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="searchQuery"
            label="Clear search"
            size="small"
            severity="secondary"
            text
            @click="searchQuery = ''"
          />
        </div>

        <DataTable
          v-else
          :value="filteredLocations"
          size="small"
          dataKey="id"
          sort-mode="single"
          removable-sort
          class="settings-locations-section__table"
          :rowClass="rowClass"
        >
          <Column field="name" header="Name" sortable>
            <template #body="{ data }">
              <span class="font-medium">{{ data.name }}</span>
            </template>
          </Column>
          <Column v-if="showInactive" header="Status" style="width: 6.5rem">
            <template #body="{ data }">
              <SettingsOrgReferenceStatusTag :item="data" />
            </template>
          </Column>
          <Column field="type" header="Type" sortable style="width: 7rem">
            <template #body="{ data }">
              {{ getLocationTypeLabel(data.type) }}
            </template>
          </Column>
          <Column header="Place" style="min-width: 10rem">
            <template #body="{ data }">
              <span class="text-surface-600 dark:text-surface-400 text-sm">
                {{ formatPlace(data) }}
              </span>
            </template>
          </Column>
          <Column header="Actions" style="width: 11rem">
            <template #body="{ data }">
              <div class="crc-table-row-actions">
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Edit'"
                  :disabled="mutating"
                  @click="openEdit(data)"
                />
                <Button
                  v-if="isOrgReferenceActive(data)"
                  icon="pi pi-ban"
                  size="small"
                  severity="warn"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Deactivate'"
                  :disabled="mutating"
                  @click="confirmDeactivate(data)"
                />
                <Button
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Delete'"
                  :disabled="mutating"
                  @click="confirmDelete(data)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </div>

    <LocationEditModal
      ref="editModalRef"
      v-model:visible="editModalVisible"
      :saving="mutating"
      @save="handleEditSave"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import FloatLabel from 'primevue/floatlabel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';
import SettingsOrgReferenceStatusTag from '../SettingsOrgReferenceStatusTag.vue';
import LocationEditModal from '../modals/LocationEditModal.vue';
import { useSettingsOrgReferenceList } from '../../composables/useSettingsOrgReferenceList';
import {
  buildOrgReferenceCountLabel,
  isOrgReferenceActive,
  orgReferenceRowClass,
  sortOrgReferenceRows,
} from '../../utils/settingsOrgReferenceUtils';
import { orgReferenceApi } from '@/api/orgReferenceApi';
import { getLocationTypeLabel, LOCATION_TYPE_OPTIONS } from '@/constants/orgReference';

defineOptions({ name: 'SettingsLocationsSection' });

defineProps({
  section: {
    type: Object,
    default: null,
  },
});

defineEmits(['close-settings']);

const toast = useToast();
const confirm = useConfirm();
const { showInactive, fetchOptions, afterMutation } = useSettingsOrgReferenceList();

const locationTypeOptions = LOCATION_TYPE_OPTIONS;
const locations = ref([]);
const loading = ref(false);
const error = ref('');
const creating = ref(false);
const mutating = ref(false);
const searchQuery = ref('');
const editModalVisible = ref(false);
const editModalRef = ref(null);

const quickAdd = reactive({
  name: '',
  type: 'office',
});

const filteredLocations = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  const base = query
    ? locations.value.filter((location) => {
        const haystack = [
          location.name,
          location.type,
          location.city,
          location.state,
          location.country,
          getLocationTypeLabel(location.type),
          isOrgReferenceActive(location) ? 'active' : 'inactive',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      })
    : locations.value;
  return sortOrgReferenceRows(base);
});

const countLabel = computed(() =>
  buildOrgReferenceCountLabel(locations.value, 'locations', showInactive.value)
);

const emptyMessage = computed(() => {
  if (searchQuery.value) return 'No locations match your search.';
  if (showInactive.value) return 'No locations found. Use quick add above to create one.';
  return 'No active locations yet. Use quick add above to create one.';
});

const rowClass = (data) => orgReferenceRowClass(data);

const formatPlace = (location) => {
  const parts = [location.city, location.state, location.country].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
};

const notifyMutationSuccess = (detail) => {
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail,
    life: 2500,
  });
};

const afterMutationReload = () => afterMutation(loadLocations);

const loadLocations = async () => {
  loading.value = true;
  error.value = '';
  try {
    const options = await fetchOptions();
    locations.value = Array.isArray(options?.locations)
      ? sortOrgReferenceRows(options.locations)
      : [];
  } catch (err) {
    error.value = err?.message || 'Failed to load locations.';
    locations.value = [];
  } finally {
    loading.value = false;
  }
};

const handleQuickAdd = async () => {
  const name = quickAdd.name?.trim();
  if (!name || creating.value) return;

  creating.value = true;
  try {
    await orgReferenceApi.locations.create({
      name,
      type: quickAdd.type || 'office',
    });
    quickAdd.name = '';
    quickAdd.type = 'office';
    notifyMutationSuccess('Location created');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to create location.',
      life: 3500,
    });
  } finally {
    creating.value = false;
  }
};

const openEdit = (location) => {
  editModalRef.value?.openEdit?.(location);
  editModalVisible.value = true;
};

const handleEditSave = async (payload) => {
  mutating.value = true;
  try {
    await orgReferenceApi.locations.update(payload);
    editModalVisible.value = false;
    notifyMutationSuccess('Location updated');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to update location.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

const confirmDeactivate = (location) => {
  confirm.require({
    header: 'Deactivate location',
    message: `"${location.name}" will be hidden from profile options. Employees and teams linked to this location keep their records.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-warning',
    accept: () => deactivateLocation(location),
  });
};

const deactivateLocation = async (location) => {
  mutating.value = true;
  try {
    await orgReferenceApi.locations.update({
      location_id: location.id,
      is_active: 0,
    });
    notifyMutationSuccess('Location deactivated');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to deactivate location.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

const confirmDelete = (location) => {
  confirm.require({
    header: 'Delete location',
    message: `Delete "${location.name}"? Employee and team location links will be cleared. This cannot be undone.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => deleteLocation(location),
  });
};

const deleteLocation = async (location) => {
  mutating.value = true;
  try {
    await orgReferenceApi.locations.delete({ location_id: location.id });
    notifyMutationSuccess('Location deleted');
    await afterMutationReload();
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err?.message || 'Failed to delete location.',
      life: 3500,
    });
  } finally {
    mutating.value = false;
  }
};

onMounted(() => {
  loadLocations();
});

watch(showInactive, () => {
  loadLocations();
});
</script>

<style scoped>
.settings-locations-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-locations-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-locations-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-locations-section__message {
  margin: 0;
}

.settings-locations-section__quick-add {
  padding: 1rem;
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  background: var(--crc-panel-bg);
}

.settings-locations-section__quick-add-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--crc-text-muted, var(--text-color-secondary));
  margin-bottom: 0.75rem;
}

.settings-locations-section__quick-add-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 10rem auto;
  gap: 0.75rem;
  align-items: end;
}

.settings-locations-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 10rem;
  border: 1px dashed var(--crc-panel-border, var(--surface-border));
  border-radius: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  text-align: center;
}

.settings-locations-section__empty-icon {
  font-size: 1.5rem;
}

.settings-locations-section__empty p {
  margin: 0;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .settings-locations-section__quick-add-form {
    grid-template-columns: 1fr;
  }
}

:deep(.settings-org-section__row--inactive) {
  opacity: 0.65;
}
</style>
